import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {map} from 'rxjs/operators';
import {environment} from '../../../../environments/environment';
import {StatesService} from '../state/states.service';
import {Token} from '../../classes/authentication/token';
import {ActivatedRoute, Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {TranslateService} from '@ngx-translate/core';
import {MatDialog} from '@angular/material/dialog';
import {UsersConstants} from '../../constants/users.constants';
import ROLES = UsersConstants.ROLES;

@Injectable({providedIn: 'root'})
export class AuthenticationService {

  constructor(private http: HttpClient,
              private stateService: StatesService,
              private router: Router,
              private route: ActivatedRoute,
              private toastr: ToastrService,
              private translate: TranslateService,
              private dialog: MatDialog) {
  }

  public login(username: string, password: string) {
    let body = new Token(username, password);
    return this.http.post<any>(`oauth/v2/token`, body)
      .pipe(map(user => {
        // login successful if there's a users in the response
        if (user) {
          // store users details and basic auth credentials in local storage
          // to keep users logged in between page refreshes
          localStorage.setItem(environment.userLocalStored, JSON.stringify(user));
          // todo :  remove after enabling WORKS mode...
          this.logoutIfUserIsClientButUser();
          this.stateService.stateLevel = this.stateService.getSelectedLevelId();
          this.stateService.stateLanguage = this.getUserLang();
          this.stateService.reloadLevelSelector.next();
        }
      }));
  }

  public logoutIfUserIsClientButUser(){
    if(this.userHasRoleClientButRoleOsmos() && !environment.clientModeEnabled){
      this.logout();
    }
  }

  public logout(deleteToken: boolean = false) {
    const currentUser = this.getStoredUser();
    const logoutUser = (backToLogin) => {
      this.dialog.closeAll();
      localStorage.removeItem(environment.userLocalStored);
      if (backToLogin) {
        this.router.navigate(['/login']);
      }
    };

    if (currentUser && currentUser.token.access_token && !deleteToken) {
      let delToken = this.http.delete(`oauth/access_token/` + currentUser.token.access_token);
      delToken.subscribe(
        data => {
          logoutUser(true);
          this.toastr.success(this.translate.instant('LOGOUT_SUCCESS'), this.translate.instant('SUCCESS'));
        },
        error => {
          logoutUser(true);
          this.toastr.error(this.translate.instant('LOGOUT_ERROR'), this.translate.instant('ERROR'));
        });

    } else {
      logoutUser(deleteToken);
    }
  }

  public getStoredUser() {
    return JSON.parse(localStorage.getItem(environment.userLocalStored));
  }

  public getToken(): string {
    return this.getStoredUser().token.access_token;
  }

  public getUserLevelId(): number {
    let levelId: number = -1;
    let currentUser = this.getStoredUser();
    if (currentUser && currentUser.level.id) {
      levelId = currentUser.level.id;
    }
    return levelId;
  }

  public currentUserIsLevelZero(): boolean {
    let currentUser = this.getStoredUser();
    if (currentUser && currentUser.level.id) {
      return currentUser.level.id === 1;
    }
    return false;
  }

  public getUserLevel(): number {
    let currentUser = this.getStoredUser();
    if (currentUser && currentUser.level.id) {
      return currentUser.level.level;
    }
    return -1;

  }

  public currentUserCanManageLevels(): boolean {
    let currentUser = this.getStoredUser();
    if (currentUser && currentUser.level.id) {
      return this.userHasRoleAdmin() && currentUser.level.level < 2;
    }
    return false;
  }

  public currentUserCanManageUsers(): boolean {
    let currentUser = this.getStoredUser();
    if (currentUser && currentUser.level.id) {
      return this.userHasRoleAdmin();
    }
    return false;
  }

  public currentUserCanManageMathsModules(): boolean {
    let currentUser = this.getStoredUser();
    if (currentUser && currentUser.level.id) {
      return (currentUser.level.id === 1 || currentUser.level.id === 14) && this.userHasRoleMaths();
    }
    return false;
  }

  public currentUserCanGetAllLevels(): boolean {
    const currentUser = this.getStoredUser();
    if (currentUser && currentUser.level.id) {
      return currentUser.level.id === 1 && this.userHasRoleSafeAllLevels();
    }
    return false;
  }

  public getUserId(): number {
    let id: number = 0;
    let currentUser = this.getStoredUser();
    if (currentUser && currentUser.id) {
      id = currentUser.id;
    }
    return id;
  }

  // get user language stored in local storage
  // For the language switched by the user while a session, use the variable stateService.stateLanguage
  public getUserLang(): string {
    let lang: string = 'en';
    let currentUser = this.getStoredUser();
    if (currentUser) {
      lang = currentUser.lang;
    }
    return lang;
  }


  public checkConfirmationToken(token: string) {
    return this.http.get(`auth/users?confirmation_token=` + token);
  }

  public userResetPassword(userId: number, token: string, new_password: string, confirm_password: string) {
    let body = {
      new_password: new_password,
      confirm_password: confirm_password
    };
    return this.http.patch(`auth/users/` + userId + `/reset-password?confirmation_token=` + token, body);
  }

  public initPassword(email: string) {
    let body = {email: email};
    return this.http.post(`auth/users/forgot-password`, body);
  }

  public userIsLevelZero() {
    let currentUser = this.getStoredUser();
    if (currentUser) {
      return currentUser.level.level === 0;
    }
    return false;
  }

  public userHasRoleClientButRoleOsmos():boolean{
    return this.userHasRoleClient() && !this.userHasRoleOsmos();
  }

  public userHasRoleMaths(): boolean {
    return this.checkUserRole(ROLES.ROLE_MATHS);
  }

  public userHasRoleOsmos(): boolean {
    return this.checkUserRole(ROLES.ROLE_OSMOS_USER);
  }

  public userHasRoleOsmosOrProduction(): boolean {
    return (this.checkUserRole(ROLES.ROLE_OSMOS_USER) || this.checkUserRole(ROLES.ROLE_PRODUCTION));
  }

  public userHasRoleOsmosOrClientOrProduction(): boolean {
    return (this.checkUserRole(ROLES.ROLE_OSMOS_USER) || this.checkUserRole(ROLES.ROLE_PRODUCTION)) || this.checkUserRole(ROLES.ROLE_CLIENT);
  }

  public userHasRoleInstaller(): boolean {
    return this.checkUserRole(ROLES.ROLE_INSTALLER);
  }

  public userHasRoleClient(): boolean {
    return this.checkUserRole(ROLES.ROLE_CLIENT);
  }

  public userHasRoleAdmin(): boolean {
    return this.checkUserRole(ROLES.ROLE_ADMIN);
  }

  public userHasRoleBe(): boolean {
    return this.checkUserRole(ROLES.ROLE_BE);
  }

  public userHasRoleProduction(): boolean {
    return this.checkUserRole(ROLES.ROLE_PRODUCTION);
  }

  public userHasRoleSafeAllLevels(): boolean {
    return this.checkUserRole(ROLES.ROLE_SAFE_ALL_LEVELS);
  }

  public userHasRoleAlarmValidator(): boolean {
    return this.checkUserRole(ROLES.ROLE_ALARM_VALIDATOR);
  }

  private checkUserRole(targetRole: string) {
    let currentUser = this.getStoredUser();
    if (currentUser) {
      return currentUser.roles.includes(targetRole);
    }
    return false;
  }

}
